import { Bubble, BubbleContent } from '@/components/ui/bubble';
import {
  Message,
  MessageAvatar,
  MessageContent,
  MessageFooter,
} from '@/components/ui/message';
import type { ConversationMessage } from '@/features/conversations/api';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { Bot, Headset, Info, User } from 'lucide-react';
import type { ComponentProps, ElementType } from 'react';
import {
  ConversationMessageMedia,
  shouldShowMessageText,
} from './conversation-message-media';
import { MessageDeliveryStatus } from './message-delivery-status';

type BubbleVariant = ComponentProps<typeof Bubble>['variant'];

const roleConfig: Record<
  string,
  {
    align: 'start' | 'end';
    variant: BubbleVariant;
    icon: ElementType;
    label: string;
    /** Extra classes on the bubble body, for colours the variants don't have. */
    contentClassName?: string;
  }
> = {
  // The customer is on the LEFT and everything we send on the RIGHT, matching
  // the mobile thread and every messaging app people already use.
  // Claire is black, staff are blue: at a glance you can see who said what.
  bot: {
    align: 'end',
    variant: 'default',
    icon: Bot,
    label: 'Bot',
    contentClassName: 'bg-foreground! text-background!',
  },
  user: { align: 'start', variant: 'muted', icon: User, label: 'User' },
  agent: {
    align: 'end',
    variant: 'default',
    icon: Headset,
    label: 'Agent',
  },
  system: { align: 'start', variant: 'outline', icon: Info, label: 'System' },
};

interface MessageBubbleProps {
  message: ConversationMessage;
  /** Show the Delivered/Read receipt. The thread passes it for the newest
   *  outbound message only; delivery failures show regardless. */
  showReceipt?: boolean;
}

/**
 * Renders a message body: any image/sticker media, plus the text content —
 * suppressing the auto-generated placeholder label when media is shown, and
 * always falling back to text so a bubble is never blank.
 */
function MessageBody({ message }: MessageBubbleProps) {
  const content = message.content ?? '';

  return (
    <div className="space-y-1.5">
      <ConversationMessageMedia message={message} />
      {shouldShowMessageText(message) && (
        <p className="whitespace-pre-wrap">{content}</p>
      )}
    </div>
  );
}

export function MessageBubble({
  message,
  showReceipt = true,
}: MessageBubbleProps) {
  const isSynced =
    message.role === 'bot' &&
    message.metadata &&
    typeof message.metadata === 'object' &&
    'synced' in message.metadata &&
    (message.metadata as Record<string, unknown>).synced === true;
  // A message a person sent from the Page/phone is synced in as `bot`, but a
  // human wrote it — it takes the staff colour.
  const config = isSynced
    ? roleConfig.agent
    : (roleConfig[message.role] ?? roleConfig.bot);
  const Icon = isSynced ? User : config.icon;
  const label = isSynced ? null : config.label;

  const sender = isSynced
    ? 'staff'
    : message.role === 'user'
      ? 'customer'
      : message.role === 'bot'
        ? 'claire'
        : message.role === 'agent'
          ? 'staff'
          : 'system';

  return (
    <Message
      align={config.align}
      data-testid="message-bubble"
      data-sender={sender}
    >
      <MessageAvatar className="size-8">
        <Icon className="size-4 text-muted-foreground" />
      </MessageAvatar>
      <MessageContent>
        <Bubble variant={config.variant} align={config.align}>
          <BubbleContent
            className={cn('rounded-lg px-3 py-2', config.contentClassName)}
          >
            <MessageBody message={message} />
          </BubbleContent>
        </Bubble>
        <MessageFooter className="px-0.5 text-[10px]">
          {label ? `${label} · ` : ''}
          {format(new Date(message.sentAt ?? message.createdAt), 'HH:mm')}
        </MessageFooter>
        {/* Receipt on its own line under the bubble, iMessage style. */}
        <MessageFooter
          data-testid="message-delivery-line"
          className="-mt-1 px-0.5 text-[10px] empty:hidden"
        >
          <MessageDeliveryStatus message={message} showReceipt={showReceipt} />
        </MessageFooter>
      </MessageContent>
    </Message>
  );
}
